/**
 * Shared hook for BOM upload → extract → review → apply flow.
 * Supports PDF, Excel, and CSV via the document text extractor.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { classifyBomDocument } from '../../../services/supplyChain/classifyBomDocument';
import { extractBomFromPdf, type ExtractedBomLine } from '../../../services/supplyChain/extractBomFromPdf';
import { bomItemsFromExtracted } from '../../../services/supplyChain/bomFromExtracted';
import { extractTextFromFile } from '../../../services/knowledge/documentUpload';
import { useStore } from '../../../store';

export interface UseBomUploadFlowOptions {
  siteId: string;
  /** When true, merge into existing plan; false = create/replace on landing. */
  defaultMode?: 'create' | 'merge';
  onUploadDocument?: (file: File) => Promise<string | undefined>;
}

export function useBomUploadFlow({
  siteId,
  defaultMode = 'create',
  onUploadDocument,
}: UseBomUploadFlowOptions) {
  const applyBomFromUpload = useStore(s => s.applyBomFromUpload);
  const addSiteDocument = useStore(s => s.addSiteDocument);
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [extractedLines, setExtractedLines] = useState<ExtractedBomLine[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [mode, setMode] = useState<'create' | 'merge'>(defaultMode);
  const [pendingDocumentId, setPendingDocumentId] = useState<string | undefined>();

  useEffect(() => {
    setMode(defaultMode);
  }, [defaultMode]);

  const openFilePicker = useCallback(() => {
    fileRef.current?.click();
  }, []);

  const processText = useCallback(async (text: string, fileName?: string, documentId?: string) => {
    const classification = classifyBomDocument(text, { fileName });
    if (!classification.isBomDocument) {
      toast.error('This document does not look like a bill of materials schedule. Try a BoM or equipment list PDF/Excel.');
      return;
    }
    const extraction = await extractBomFromPdf(text);
    if (extraction.lines.length === 0) {
      toast.error('No BoM line items could be extracted.');
      return;
    }
    setExtractedLines(extraction.lines);
    setWarnings(extraction.warnings);
    setPendingDocumentId(documentId);
    setReviewOpen(true);
  }, []);

  const handleFile = useCallback(async (file: File) => {
    setBusy(true);
    try {
      let documentId: string | undefined;
      if (onUploadDocument) {
        documentId = await onUploadDocument(file);
      } else {
        const doc = await addSiteDocument(siteId, file);
        documentId = doc.id;
        toast.success('Document uploaded.');
      }

      const text = await extractTextFromFile(file);
      await processText(text, file.name, documentId);
    } catch (e) {
      toast.error(`BoM upload failed: ${(e as Error)?.message ?? 'unknown'}`);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [addSiteDocument, onUploadDocument, processText, siteId]);

  const confirmApply = useCallback(async (lines: ExtractedBomLine[]) => {
    setBusy(true);
    try {
      const items = bomItemsFromExtracted(lines, { documentId: pendingDocumentId });
      const res = await applyBomFromUpload({
        siteId,
        items,
        mode,
        documentId: pendingDocumentId,
      });
      if (res.ok) {
        toast.success(`BoM applied — ${res.itemCount} items.`);
        setReviewOpen(false);
        setExtractedLines([]);
        setWarnings([]);
      } else {
        toast.error(res.error);
      }
    } finally {
      setBusy(false);
    }
  }, [applyBomFromUpload, mode, pendingDocumentId, siteId]);

  return {
    fileRef,
    busy,
    reviewOpen,
    extractedLines,
    warnings,
    mode,
    setMode,
    openFilePicker,
    handleFile,
    confirmApply,
    cancelReview: () => setReviewOpen(false),
    processPastedText: processText,
    applyExtractedLines: confirmApply,
    setExtractedFromExternal: (lines: ExtractedBomLine[], documentId?: string, extractionWarnings?: string[]) => {
      setExtractedLines(lines);
      setWarnings(extractionWarnings ?? []);
      setPendingDocumentId(documentId);
      setReviewOpen(true);
    },
  };
}
